// Agent API - Connects to the sandbox-backend agent endpoints
// Supports both stubbed responses (for development) and real backend

import type {
  AgentQueryResponse,
  AgentStreamEvent,
  RetrievedContext,
  CodeBlock,
  AgentSettings,
  PipelineStep,
  V2AssetSpecPayload,
  V2MaterializedAsset,
} from './types';
import type { TestAssetRef } from '@gotti/sandbox-core';
import type { GboxRunbook } from '@gotti/sandbox-shared';

// API configuration
const API_BASE_URL = import.meta.env.VITE_AGENT_API_URL || '/api/agent';

// Feature flag: Use real backend or stubbed responses
// Set VITE_USE_REAL_AGENT=true in .env to use real backend
const USE_REAL_AGENT = import.meta.env.VITE_USE_REAL_AGENT === 'true';

// Session ID for maintaining conversation state
let currentSessionId: string | null = null;

// AbortController for the current in-flight SSE request.
// Allows cancellation on component unmount, navigation, or explicit cancel.
let currentAbortController: AbortController | null = null;

/**
 * Cancel any in-flight agent query.
 * Safe to call when no query is active (no-op).
 */
export function cancelCurrentQuery(): void {
  if (currentAbortController) {
    currentAbortController.abort();
    currentAbortController = null;
  }
}

/**
 * Reset the session ID. Called when the user clears chat history
 * so the backend starts a fresh conversation.
 */
export function resetSessionId(): void {
  currentSessionId = null;
}

/**
 * Post test results back to the server for agent-delegated test execution.
 *
 * `failures[]` is the v2-pipeline structured failure list — one entry per
 * failed test, with the suite + test names + error string so the
 * fix-phase planner can decide which source file to rewrite. The legacy
 * backend ignores this field; v2 reads it via the resolveDeferredV2Test
 * resolver.
 */
export async function postTestResults(
  sessionId: string,
  results: {
    passed: boolean;
    totalTests: number;
    testsPassed: number;
    testsFailed: number;
    duration: number;
    fileResults: Array<{
      file: string;
      passed: boolean;
      tests: number;
      failures: number;
      errors: string[];
    }>;
    failures?: Array<{
      file: string;
      suite: string;
      testName: string;
      error: string;
      /** Normalized virtual file:line:col call-sites parsed from the stack. */
      errorFrames?: string[];
      /** Distinct system-code console calls made during this test (deduped). */
      logs?: Array<{ level: string; site: string; message: string; count: number }>;
    }>;
    /**
     * Line-coverage summary from the in-browser `CoverageTracker`, trimmed to
     * the deep agent's minimal `CoverageSummary` shape (percentage + per-file
     * percentage + uncovered line numbers — no per-line hit data) so this wire
     * payload stays small. Feeds the deep agent's coverage guardrail
     * (`graph.ts`) the same way the node runner's coverage already does;
     * absent when no test run has collected coverage yet.
     */
    coverage?: {
      percentage: number;
      files: Array<{ path: string; percentage: number; uncoveredLines: number[] }>;
    };
  },
): Promise<void> {
  await fetch(`${API_BASE_URL}/chat/${encodeURIComponent(sessionId)}/test-results`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(results),
  });
}

export interface SendQueryOptions {
  query: string;
  settings: AgentSettings;
  // Current sandbox code context for RAG
  sandboxContext?: {
    files: { path: string; content: string }[];
    designSpecs?: Array<{
      id?: string;
      title: string;
      category: string;
      systemName?: string;
      body: string;
      relatedCodeSymbols?: Array<{ path: string; symbol?: string }>;
      relatedSpecIds?: string[];
    }>;
    runbooks?: GboxRunbook[];
  };
  // Callback for streaming updates
  onEvent?: (event: AgentStreamEvent) => void;
}

/**
 * Check if the agent backend is available.
 */
export async function checkAgentConnection(): Promise<boolean> {
  if (!USE_REAL_AGENT) {
    console.log('[AgentAPI] Using stubbed responses (set VITE_USE_REAL_AGENT=true for real backend)');
    return true;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/health`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    if (!response.ok) {
      console.warn('[AgentAPI] Backend health check failed:', response.status);
      return false;
    }

    const data = await response.json();
    console.log('[AgentAPI] Backend connected:', data);
    return data.status === 'ok';
  } catch (error) {
    console.warn('[AgentAPI] Failed to connect to backend:', error);
    return false;
  }
}

/**
 * Send a query to the agent and stream the response.
 */
export async function sendAgentQuery(options: SendQueryOptions): Promise<AgentQueryResponse> {
  if (!USE_REAL_AGENT) {
    return sendAgentQueryStubbed(options);
  }

  return sendAgentQueryReal(options);
}

/**
 * Real implementation using SSE streaming.
 *
 * The backend (Hono streamSSE) emits events in standard SSE format:
 *   event: <type>\n
 *   data: <json>\n\n
 *
 * We parse event/data pairs correctly and map each AISessionEvent type
 * to the frontend AgentStreamEvent discriminated union.
 */
async function sendAgentQueryReal(options: SendQueryOptions): Promise<AgentQueryResponse> {
  const { query, settings, sandboxContext, onEvent } = options;

  console.log('[AgentAPI] Sending query to backend:', query);

  // Convert files array to object
  const files: Record<string, string> = {};
  if (sandboxContext?.files) {
    for (const file of sandboxContext.files) {
      files[file.path] = file.content;
    }
  }

  // Cancel any previous in-flight request before starting a new one
  cancelCurrentQuery();
  const controller = new AbortController();
  currentAbortController = controller;

  try {
    const response = await fetch(`${API_BASE_URL}/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: query,
        files,
        designSpecs: sandboxContext?.designSpecs,
        runbooks: sandboxContext?.runbooks,
        sessionId: currentSessionId,
        // C1 fix: Forward settings from the UI settings panel to the backend
        settings: settings ? {
          model: settings.model,
          temperature: settings.temperature,
          maxTokens: settings.maxTokens,
        } : undefined,
      }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    // Handle SSE stream — parse event: / data: pairs
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let requestId = '';

    if (reader) {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by double newline
        let boundary: number;
        while ((boundary = buffer.indexOf('\n\n')) !== -1) {
          const block = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);

          // Parse event type and data from the block
          let eventType = '';
          let eventData = '';
          for (const line of block.split('\n')) {
            if (line.startsWith('event:')) {
              eventType = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
              eventData = line.slice(5).trim();
            }
          }

          if (!eventData) continue;

          try {
            const data = JSON.parse(eventData);
            mapBackendEventToFrontend(eventType, data, onEvent, (sid) => {
              currentSessionId = sid;
              requestId = sid;
            });
          } catch (e) {
            console.warn('[AgentAPI] Failed to parse SSE data:', eventData);
          }
        }
      }
    }

    return { requestId, success: true };
  } catch (error) {
    // Don't report abort as an error — it's intentional cancellation
    if (error instanceof DOMException && error.name === 'AbortError') {
      console.log('[AgentAPI] Request cancelled');
      return { requestId: '', success: false, error: 'Request cancelled' };
    }
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    console.error('[AgentAPI] Error:', errorMessage);
    onEvent?.({ type: 'error', data: { message: errorMessage } });
    return { requestId: '', success: false, error: errorMessage };
  } finally {
    // Clear controller reference so cancelCurrentQuery() becomes a no-op
    if (currentAbortController === controller) {
      currentAbortController = null;
    }
  }
}

/**
 * Maps a backend AISessionEvent to the frontend AgentStreamEvent union.
 *
 * Backend event types (from AISession.queryStream):
 *   session       → save sessionId
 *   indexing       → status(indexing)
 *   retrieving     → status(retrieving)
 *   context        → context
 *   planning       → status(planning)
 *   executing      → status(executing)
 *   chunk          → chunk
 *   file:modified  → file_modified
 *   complete       → complete
 *   error          → error
 *   done           → (ignored — stream ends)
 */
/** Format a line-range suffix for readFile tool display, e.g. ` (L24–106)` */
function formatReadFileRange(toolName: string, args: Record<string, unknown> | undefined): string {
  if (!args || toolName !== 'readFile') return '';
  const startLine = typeof args.startLine === 'number' ? args.startLine : undefined;
  const endLine = typeof args.endLine === 'number' ? args.endLine : undefined;
  if (startLine != null && endLine != null) return ` (L${startLine}–${endLine})`;
  if (startLine != null) return ` (from L${startLine})`;
  const chunk = typeof args.chunk === 'number' ? args.chunk : undefined;
  if (chunk != null) return ` (chunk ${chunk})`;
  return '';
}

// Exported for unit tests (the SSE-frame → AgentStreamEvent translation is
// otherwise only reachable through a live fetch stream).
export function mapBackendEventToFrontend(
  eventType: string,
  data: Record<string, unknown>,
  onEvent: ((event: AgentStreamEvent) => void) | undefined,
  setSessionId: (sid: string) => void,
): void {
  switch (eventType) {
    case 'session':
      if (typeof data.sessionId === 'string') setSessionId(data.sessionId);
      break;

    case 'indexing':
      onEvent?.({ type: 'status', data: { step: 'indexing', message: (data.message as string) || 'Indexing files…' } });
      break;

    case 'retrieving':
      onEvent?.({ type: 'status', data: { step: 'retrieving', message: `Retrieving context for "${data.query}"…` } });
      break;

    case 'context':
      // Convert paths+scores into RetrievedContext[]
      if (Array.isArray(data.paths)) {
        const scores = Array.isArray(data.scores) ? data.scores as number[] : [];
        const contexts: RetrievedContext[] = (data.paths as string[]).map((path, i) => ({
          id: `ctx-${i}`,
          source: 'codebase' as const,
          path,
          snippet: '',
          relevance: scores[i] ?? 0,
        }));
        onEvent?.({ type: 'context', data: contexts });
      }
      break;

    case 'planning': {
      // Build a summary string from the actions array so completedSteps can show it
      const actions = Array.isArray(data.actions) ? data.actions as Array<{ tool: string; reason?: string }> : [];
      const actionsSummary = actions.length > 0
        ? actions.map(a => a.reason ? `${a.tool}: ${a.reason}` : a.tool).join(' → ')
        : undefined;
      const reasoning = typeof data.reasoning === 'string' ? data.reasoning : undefined;
      onEvent?.({
        type: 'status',
        data: {
          step: 'planning',
          message: `Planned ${actions.length} action(s)`,
          detail: { actions: data.actions, summary: actionsSummary, reasoning },
        },
      });
      break;
    }

    case 'executing': {
      // If we receive an executing event without a tool field it's an
      // upstream bug — drop it rather than render the literal "unknown"
      // badge in the panel. The matching tool:result will arrive too;
      // dropping the start prevents a half-rendered call.
      const tool = typeof data.tool === 'string' && data.tool.length > 0 ? data.tool : undefined;
      if (!tool) {
        console.warn('[agentApi] executing event missing tool field — dropping (would render as "unknown")');
        break;
      }
      onEvent?.({
        type: 'status',
        data: {
          step: 'executing',
          message: `Executing ${tool}…`,
          tool,
          detail: data.args as Record<string, unknown>,
        },
      });
      break;
    }

    case 'tool:result': {
      const toolName = typeof data.tool === 'string' && data.tool.length > 0 ? data.tool : undefined;
      if (!toolName) {
        console.warn('[agentApi] tool:result event missing tool field — dropping (would render as "unknown")');
        break;
      }
      const success = data.success !== false;
      const path = data.path as string | undefined;
      const toolMessage = typeof data.message === 'string' ? data.message : undefined;
      // Use the tool's own human-readable message if available, otherwise fallback
      const resultMsg = success
        ? (toolMessage || (path ? `✓ ${toolName} → ${path}` : `✓ ${toolName}`))
        : `✗ ${toolName} failed`;
      onEvent?.({
        type: 'status',
        data: {
          step: 'executing',
          message: resultMsg,
          tool: toolName,
          detail: { success, path, toolMessage },
        },
      });
      break;
    }

    case 'validating':
      onEvent?.({
        type: 'status',
        data: {
          step: 'validating',
          message: `Validating ${data.fileCount || 0} file(s)…`,
        },
      });
      break;

    case 'validated': {
      const errors = Array.isArray(data.errors) ? data.errors as string[] : [];
      const willRetry = data.willRetry === true;
      const valMsg = errors.length === 0
        ? '✓ Validation passed'
        : `${errors.length} error(s)${willRetry ? ' — retrying…' : ''}`;
      onEvent?.({
        type: 'status',
        data: {
          step: 'validating',
          message: valMsg,
          detail: { errors, willRetry },
        },
      });
      break;
    }

    case 'diagnosing': {
      const errorCount = typeof data.errorCount === 'number' ? data.errorCount : 0;
      const errors = Array.isArray(data.errors)
        ? (data.errors as unknown[]).filter((e): e is string => typeof e === 'string')
        : [];
      onEvent?.({
        type: 'status',
        data: {
          step: 'diagnosing',
          message: `Diagnosing ${errorCount} error(s)…`,
          detail: { errors },
        },
      });
      break;
    }

    case 'chunk':
      onEvent?.({ type: 'chunk', data: data.content as string });
      break;

    case 'file:modified': {
      const filePath = typeof data.path === 'string' ? data.path : '';
      const fileContent = typeof data.content === 'string' ? data.content : '';
      if (filePath) {
        onEvent?.({ type: 'file_modified', data: { path: filePath, content: fileContent } });
      } else {
        console.warn('[AgentAPI] file:modified event missing path, skipping');
      }
      break;
    }

    case 'files:sync': {
      // AUTHORITATIVE full snapshot of the AI's working files. Consumers REPLACE
      // their mirror with this set (incl. deletes) so the TESTS panel + runner
      // always reflect EXACTLY what the worker holds — even for silent bulk
      // mutations that emit no per-file file:modified.
      const rawFiles = Array.isArray(data.files) ? data.files as Array<{ path?: unknown; content?: unknown }> : [];
      const files = rawFiles
        .filter((f) => typeof f?.path === 'string')
        .map((f) => ({ path: f.path as string, content: typeof f.content === 'string' ? f.content : '' }));
      onEvent?.({ type: 'files_sync', data: { files } });
      break;
    }

    case 'complete': {
      const response = (data.response as string) || '';
      // The backend sends `modifiedFiles` as `Array<{path: string; content: string}>` —
      // each entry carries the FULL post-pipeline file content. The
      // markdown-fence extractor (`extractCodeBlocks(response)`) only sees what
      // the LLM put in its chat text — usually stub code, not the rich code
      // its createSystem/replaceInFile tool calls produced. Prefer the
      // structured contents and only fall back to fence-extracted blocks
      // when the backend didn't provide them.
      type RichModifiedFile = { path: string; content: string };
      const rawModified = (data.modifiedFiles as unknown) ?? [];
      const isStructured =
        Array.isArray(rawModified) &&
        rawModified.length > 0 &&
        typeof (rawModified as { path?: unknown }[])[0]?.path === 'string';

      let codeBlocks: CodeBlock[];
      let modifiedFiles: string[];
      if (isStructured) {
        const structured = rawModified as RichModifiedFile[];
        codeBlocks = structured.map((f, i) => ({
          id: `backend-file-${i}`,
          language: f.path.endsWith('.tsx') ? 'tsx' : 'ts',
          filename: f.path.replace(/^\/+/, ''),
          code: f.content,
          applied: false,
        }));
        modifiedFiles = structured.map(f => f.path);
      } else {
        codeBlocks = extractCodeBlocks(response);
        modifiedFiles = (rawModified as string[]) || [];
      }
      onEvent?.({
        type: 'complete',
        data: {
          codeBlocks,
          modifiedFiles,
        },
      });
      break;
    }

    case 'error':
      onEvent?.({
        type: 'error',
        data: {
          message: (data.message as string) || 'Unknown error',
          // Pass the per-item error array through so the chat bubble can
          // render the actual diagnostics, not just a summary count.
          errors: Array.isArray(data.errors) ? (data.errors as string[]) : undefined,
        },
      });
      break;

    case 'done':
      // Stream finished — no-op
      break;

    // ── Granular pipeline events ────────────────────────────────────────
    case 'step:start':
      onEvent?.({
        type: 'status',
        data: {
          step: (data.step as string) as PipelineStep,
          message: data.message as string,
        },
      });
      break;

    case 'step:end': {
      const extraData = data.data as Record<string, unknown> | undefined;
      onEvent?.({
        type: 'status',
        data: {
          step: (data.step as string) as PipelineStep,
          message: data.message as string,
          detail: { durationMs: data.durationMs, ...(extraData ?? {}) },
        },
      });
      break;
    }

    case 'tool:start': {
      const args = data.args as Record<string, unknown> | undefined;
      const lineRange = formatReadFileRange(data.tool as string, args);
      // Custom-phrase the start message for tools whose UX intent is
      // distinct enough that "Running runTests…" feels mechanical. Each
      // phrasing override is keyed off the tool name; everything else
      // falls through to the generic verb-form.
      const message =
        data.tool === 'runTests'
          ? 'Running tests…'
          : `Running ${data.tool}…${data.file ? ` → ${data.file}` : ''}${lineRange}`;
      onEvent?.({
        type: 'status',
        data: {
          step: 'executing',
          message,
          tool: data.tool as string,
          detail: { phase: 'start', file: data.file, index: data.index, total: data.total, args: data.args },
        },
      });
      break;
    }

    case 'tool:end': {
      const endArgs = data.args as Record<string, unknown> | undefined;
      const endLineRange = formatReadFileRange(data.tool as string, endArgs);
      // For runTests, the result payload carries pass/fail counts. Surface
      // them in the status message so the trail's collapsed row reads
      // "✓ 12/15 tests passed" or "✗ 3 of 15 tests failed" instead of
      // a flat "✓ runTests". Falls back to the generic verb-form for
      // every other tool.
      let message: string;
      if (data.tool === 'runTests') {
        const result = data.result as
          | {
              passed?: boolean;
              testsPassed?: number;
              testsFailed?: number;
              totalTests?: number;
            }
          | undefined;
        const total = result?.totalTests;
        const passed = result?.testsPassed;
        const failed = result?.testsFailed;
        if (data.success && result?.passed && typeof passed === 'number' && typeof total === 'number') {
          message = `✓ ${passed}/${total} test${total !== 1 ? 's' : ''} passed`;
        } else if (typeof failed === 'number' && typeof total === 'number' && failed > 0) {
          message = `✗ ${failed}/${total} test${total !== 1 ? 's' : ''} failed`;
        } else if (data.success) {
          message = '✓ tests passed';
        } else {
          message = `✗ tests failed${data.message ? `: ${data.message}` : ''}`;
        }
      } else {
        message = data.success
          ? `✓ ${data.tool}${data.file ? ` → ${data.file}` : ''}${endLineRange}`
          : `✗ ${data.tool} failed${data.message ? `: ${data.message}` : ''}`;
      }
      onEvent?.({
        type: 'status',
        data: {
          step: 'executing',
          message,
          tool: data.tool as string,
          detail: {
            phase: 'end',
            success: data.success,
            file: data.file,
            durationMs: data.durationMs,
            args: data.args,
            result: data.result,
            fileContentBefore: data.fileContentBefore,
          },
        },
      });
      break;
    }

    case 'llm:start':
      onEvent?.({
        type: 'status',
        data: {
          step: (data.step as string) as PipelineStep,
          message: `Calling LLM${data.model ? ` (${data.model})` : ''}…`,
          detail: { phase: 'llm:start', model: data.model, label: data.label },
        },
      });
      break;

    case 'llm:end': {
      const tokens = data.tokens as { prompt?: number; completion?: number; total?: number } | undefined;
      onEvent?.({
        type: 'status',
        data: {
          step: (data.step as string) as PipelineStep,
          message: `LLM responded${tokens?.total ? ` (${tokens.total} tokens)` : ''}`,
          detail: { phase: 'llm:end', durationMs: data.durationMs, tokens },
        },
      });
      break;
    }

    case 'retry':
      onEvent?.({
        type: 'status',
        data: {
          step: 'diagnosing',
          message: data.message as string,
          detail: {
            phase: 'retry',
            iteration: data.iteration,
            maxIterations: data.maxIterations,
            errorCount: data.errorCount,
            errorFiles: data.errorFiles,
          },
        },
      });
      break;

    case 'progress':
      onEvent?.({
        type: 'status',
        data: {
          step: 'executing',
          message: data.message as string,
          detail: data.detail ? { info: data.detail } : undefined,
        },
      });
      break;

    case 'test:request':
      onEvent?.({
        type: 'test_request',
        data: {
          sessionId: data.sessionId as string,
          files: data.files as Record<string, string>,
          // Optional subset filter (closed inner loop runs one cluster's tests).
          testFilePaths: data.testFilePaths as string[] | undefined,
          // Generated assets the run must preload (worker OMITS the field
          // entirely when there are none — undefined keeps the session-store-
          // only behavior in createTestAssetSource).
          assets: data.assets as TestAssetRef[] | undefined,
        },
      });
      break;

    case 'test:progress':
      onEvent?.({
        type: 'test_progress',
        data: {
          file: data.file as string,
          phase: data.phase as 'run-start' | 'test-start' | 'test-end' | 'run-end',
          suiteName: data.suiteName as string | undefined,
          testName: data.testName as string | undefined,
          status: data.status as 'passed' | 'failed' | 'error' | 'skipped' | undefined,
          durationMs: data.durationMs as number | undefined,
          error: data.error as string | undefined,
          failureSource: data.failureSource as
            | { file: string; line: number; column?: number; snippet: string }
            | undefined,
          totalTests: data.totalTests as number | undefined,
          totalPassed: data.totalPassed as number | undefined,
          totalFailed: data.totalFailed as number | undefined,
        },
      });
      break;

    // ── Granular diagnose / reflect / thinking events ──
    //
    // The backend (sandbox-ai's AISession + mapPipelineEvent) emits
    // these with the chat UI rendering shape already baked in — we
    // just rename `:` → `_` for frontend type-discriminator legibility
    // (TypeScript prefers identifiers; SSE keys carry colons by Hono
    // convention). One arm per event type so each can be rendered as a
    // distinct sub-row in the chat history.
    case 'llm:thinking':
      onEvent?.({
        type: 'thinking',
        data: {
          step: data.step as string,
          model: data.model as string | undefined,
          source: data.source as 'anthropic-thinking' | 'openai-reasoning' | 'structured-reasoning' | 'heuristic-narrative',
          content: data.content as string,
          index: data.index as number | undefined,
        },
      });
      break;

    case 'diagnose:stage':
      onEvent?.({
        type: 'diagnose_stage',
        data: {
          stage: data.stage as 'heuristic' | 'llm' | 'merge' | 're-retrieve' | 'complete',
          phase: data.phase as 'start' | 'end',
          message: data.message as string,
          durationMs: data.durationMs as number | undefined,
          detail: data.data as Record<string, unknown> | undefined,
        },
      });
      break;

    case 'diagnose:error':
      onEvent?.({
        type: 'diagnose_error',
        data: {
          index: data.index as number,
          total: data.total as number,
          message: data.message as string,
          category: data.category as string,
          confidence: data.confidence as number,
          file: data.file as string | undefined,
          line: data.line as number | undefined,
          offendingSymbol: data.offendingSymbol as string | undefined,
          knownCorrection: data.knownCorrection as string | undefined,
          matchedPattern: data.matchedPattern as string | undefined,
        },
      });
      break;

    case 'diagnose:correction':
      onEvent?.({
        type: 'diagnose_correction',
        data: {
          index: data.index as number,
          total: data.total as number,
          file: data.file as string,
          description: data.description as string,
          problemDescription: data.problemDescription as string | undefined,
          fixNarrative: data.fixNarrative as string | undefined,
          confidence: data.confidence as number,
          source: data.source as 'heuristic' | 'llm' | 'merged',
        },
      });
      break;

    case 'reflect:result':
      onEvent?.({
        type: 'reflect_result',
        data: {
          iteration: data.iteration as number,
          goalProgress: data.goalProgress as 'achieved' | 'partial' | 'blocked' | 'regressed',
          evidence: data.evidence as string,
          blockingPattern: data.blockingPattern as string | undefined,
          recommendation: data.recommendation as 'respond' | 'retry-same' | 'replan' | 'decompose' | 'abandon',
          rationale: data.rationale as string,
          subGoals: data.subGoals as string[] | undefined,
          recursionDepth: data.recursionDepth as number,
          reflectionCount: data.reflectionCount as number,
        },
      });
      break;

    // ── v2 recursive-tree pipeline events ───────────────────────────────
    case 'v2:workflow:start':
      onEvent?.({ type: 'v2:workflow:start', data: { rootId: data.rootId as string } });
      break;

    case 'v2:workflow:end':
      onEvent?.({ type: 'v2:workflow:end' });
      break;

    case 'v2:node:created':
      onEvent?.({
        type: 'v2:node:created',
        data: {
          id: data.id as string,
          parentId: (data.parentId as string | null) ?? null,
          depth: data.depth as number,
          phase: (data.phase as 'design' | 'tests' | 'code' | null) ?? null,
          title: data.title as string,
          purpose: data.purpose as string,
          kind: data.kind as 'root' | 'phase' | 'group' | 'file' | 'pending',
        },
      });
      break;

    case 'v2:node:status':
      onEvent?.({
        type: 'v2:node:status',
        data: {
          id: data.id as string,
          status: data.status as 'pending' | 'planning' | 'running' | 'done' | 'failed',
          kind: data.kind as 'root' | 'phase' | 'group' | 'file' | 'pending' | undefined,
        },
      });
      break;

    case 'v2:node:file':
      onEvent?.({
        type: 'v2:node:file',
        data: {
          id: data.id as string,
          path: data.path as string,
          contentLength: data.contentLength as number,
        },
      });
      break;

    case 'v2:node:error':
      onEvent?.({
        type: 'v2:node:error',
        data: { id: data.id as string, message: data.message as string },
      });
      break;

    case 'v2:node:chunk':
      onEvent?.({
        type: 'v2:node:chunk',
        data: {
          id: data.id as string,
          delta: data.delta as string,
          accumulatedLength: data.accumulatedLength as number,
        },
      });
      break;

    case 'v2:node:activity':
      onEvent?.({
        type: 'v2:node:activity',
        data: { id: data.id as string, message: data.message as string },
      });
      break;

    case 'v2:node:tool':
      onEvent?.({
        type: 'v2:node:tool',
        data: {
          id: data.id as string,
          seq: data.seq as number,
          action: data.action as string,
          target: data.target as string | undefined,
          thought: data.thought as string | undefined,
          status: data.status as 'ok' | 'rejected' | 'error',
          detail: data.detail as string | undefined,
        },
      });
      break;

    case 'v2:node:tokens':
      onEvent?.({
        type: 'v2:node:tokens',
        data: {
          id: data.id as string,
          inputTokens: data.inputTokens as number,
          outputTokens: data.outputTokens as number,
          cachedInputTokens: (data.cachedInputTokens as number) ?? 0,
          estimated: data.estimated as boolean,
        },
      });
      break;

    case 'v2:node:designSpec':
      onEvent?.({
        type: 'v2:node:designSpec',
        data: {
          id: data.id as string,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          spec: data.spec as any,
        },
      });
      break;

    case 'v2:node:runbook':
      onEvent?.({
        type: 'v2:node:runbook',
        data: {
          id: data.id as string,
          runbook: data.runbook as GboxRunbook,
        },
      });
      break;

    // ── v2 asset-generation events ──────────────────────────────────────
    // Worker relay (PipelineWorker) frames: `id` is the owning tree-node id.
    case 'v2:asset:spec':
      onEvent?.({
        type: 'v2:asset:spec',
        data: { id: data.id as string, spec: data.spec as V2AssetSpecPayload },
      });
      break;

    case 'v2:asset:created':
      // Carries the FULL MaterializedAsset (incl. inline base64 when small),
      // mirroring how file:modified carries full content. The consumer
      // (AgentContext) folds it into the session asset store.
      onEvent?.({
        type: 'v2:asset:created',
        data: { id: data.id as string, asset: data.asset as V2MaterializedAsset },
      });
      break;

    case 'v2:asset:failed':
      onEvent?.({
        type: 'v2:asset:failed',
        data: {
          id: data.id as string,
          specId: data.specId as string,
          path: data.path as string,
          message: data.message as string,
        },
      });
      break;

    default:
      console.log('[AgentAPI] Unknown SSE event:', eventType, data);
  }
}

/**
 * Extract code blocks from markdown response.
 */
function extractCodeBlocks(text: string): CodeBlock[] {
  const blocks: CodeBlock[] = [];
  const codeBlockRegex = /```(\w+)?\n([\s\S]*?)```/g;
  let match;
  let id = 0;

  while ((match = codeBlockRegex.exec(text)) !== null) {
    const language = match[1] || 'typescript';
    const code = match[2].trim();
    
    // Try to extract filename from first line comment
    let filename: string | undefined;
    const firstLine = code.split('\n')[0];
    if (firstLine.includes('//') && firstLine.includes('/')) {
      filename = firstLine.replace(/\/\/\s*/, '').trim();
    }

    blocks.push({
      id: `code-${++id}`,
      language,
      filename,
      code,
      applied: false,
    });
  }

  return blocks;
}

/**
 * Stubbed implementation for development/testing.
 * Emits the same event sequence the real backend would.
 */
async function sendAgentQueryStubbed(options: SendQueryOptions): Promise<AgentQueryResponse> {
  const { query, sandboxContext, onEvent } = options;

  console.log('[AgentAPI] Sending query (stubbed):', query);
  console.log('[AgentAPI] Context files:', sandboxContext?.files.map(f => f.path));

  const requestId = `req-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  try {
    // 1. Indexing phase
    onEvent?.({ type: 'status', data: { step: 'indexing', message: 'Checking file index…' } });
    await simulateDelay(300);

    // 2. Retrieval phase
    onEvent?.({ type: 'status', data: { step: 'retrieving', message: `Retrieving context for "${query}"…` } });
    await simulateDelay(800);

    const retrievedContext = await simulateContextRetrieval(query);
    onEvent?.({ type: 'context', data: retrievedContext });

    // 3. Planning phase
    onEvent?.({ type: 'status', data: { step: 'planning', message: 'Planning actions…' } });
    await simulateDelay(400);

    // 4. Execution phase
    const response = generateStubResponse(query);
    if (response.codeBlocks.length > 0) {
      for (const block of response.codeBlocks) {
        onEvent?.({
          type: 'status',
          data: {
            step: 'executing',
            message: `Writing ${block.filename || 'code'}…`,
            tool: 'writeFile',
            detail: { filename: block.filename },
          },
        });
        await simulateDelay(300);

        if (block.filename) {
          onEvent?.({ type: 'file_modified', data: { path: block.filename, content: block.code } });
        }
      }
    }

    // 5. Streaming response phase
    onEvent?.({ type: 'status', data: { step: 'streaming', message: 'Generating response…' } });
    await simulateDelay(200);

    const chunks = chunkText(response.text, 10);
    for (const chunk of chunks) {
      await simulateDelay(30);
      onEvent?.({ type: 'chunk', data: chunk });
    }

    // Emit code blocks
    for (const block of response.codeBlocks) {
      onEvent?.({ type: 'code', data: block });
    }

    // 6. Complete
    const modifiedFiles = response.codeBlocks
      .map(b => b.filename)
      .filter((f): f is string => !!f);

    onEvent?.({ type: 'complete', data: { codeBlocks: response.codeBlocks, modifiedFiles } });

    return { requestId, success: true };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    onEvent?.({ type: 'error', data: { message: errorMessage } });
    return { requestId, success: false, error: errorMessage };
  }
}

/**
 * Apply generated code to the sandbox.
 * Returns the original content for creating a checkpoint.
 */
export async function applyCodeToSandbox(
  codeBlock: CodeBlock,
  onApply: (filename: string, code: string) => void,
  getFileContent: (filename: string) => string | null
): Promise<{ success: boolean; originalContent: string | null }> {
  console.log('[AgentAPI] Applying code:', codeBlock.filename);
  
  if (codeBlock.filename && codeBlock.code) {
    // Get original content before applying
    const originalContent = getFileContent(codeBlock.filename);
    
    // Apply the new code
    onApply(codeBlock.filename, codeBlock.code);
    
    return { success: true, originalContent };
  }
  
  return { success: false, originalContent: null };
}

/**
 * Revert code to a checkpoint's original content.
 */
export async function revertToCheckpoint(
  filename: string,
  originalContent: string,
  onApply: (filename: string, code: string) => void
): Promise<boolean> {
  console.log('[AgentAPI] Reverting to checkpoint:', filename);
  onApply(filename, originalContent);
  return true;
}

// ============================================================================
// Stub Helpers - Generate fake responses for development
// ============================================================================

async function simulateDelay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

async function simulateContextRetrieval(query: string): Promise<RetrievedContext[]> {
  // Generate fake retrieved context based on query keywords
  const contexts: RetrievedContext[] = [];
  
  if (query.toLowerCase().includes('player') || query.toLowerCase().includes('movement')) {
    contexts.push({
      id: 'ctx-1',
      source: 'codebase',
      path: 'shared/components/PlayerMovementComponent.ts',
      snippet: 'export class PlayerMovementComponent extends Component {\n  position: Vector2;\n  velocity: Vector2;\n  ...\n}',
      relevance: 0.95,
    });
  }
  
  if (query.toLowerCase().includes('input') || query.toLowerCase().includes('keyboard')) {
    contexts.push({
      id: 'ctx-2',
      source: 'codebase',
      path: 'client/systems/InputSystem.ts',
      snippet: 'export default class InputSystem extends ClientSystem() {\n  handleKey(key: string, pressed: boolean) {\n    ...\n  }\n}',
      relevance: 0.88,
    });
  }
  
  if (query.toLowerCase().includes('system')) {
    contexts.push({
      id: 'ctx-3',
      source: 'docs',
      path: 'docs/systems.md',
      snippet: '# Systems\n\nSystems contain the game logic. Client systems handle input and rendering...',
      relevance: 0.82,
    });
  }
  
  // Always include at least one context
  if (contexts.length === 0) {
    contexts.push({
      id: 'ctx-default',
      source: 'templates',
      path: 'templates/sandbox-player-movement-netcode',
      snippet: 'Default template with client-side prediction and server reconciliation.',
      relevance: 0.7,
    });
  }
  
  return contexts;
}

interface StubResponse {
  text: string;
  codeBlocks: CodeBlock[];
}

function generateStubResponse(query: string): StubResponse {
  const queryLower = query.toLowerCase();
  
  // Generate contextual stub responses
  if (queryLower.includes('add') && queryLower.includes('jump')) {
    return {
      text: `I can help you add a jump mechanic! Here's how to modify the PlayerMovementComponent to support jumping:\n\nThe key changes are:\n1. Add a \`grounded\` state and \`jumpVelocity\` property\n2. Apply gravity in the update loop\n3. Handle jump input in the InputSystem\n\nHere's the code:`,
      codeBlocks: [
        {
          id: 'code-1',
          language: 'typescript',
          filename: 'shared/components/PlayerMovementComponent.ts',
          code: `// Add to MovementConfig interface:
jumpVelocity: number;  // Initial upward velocity when jumping
gravity: number;       // Gravity acceleration

// Add to class properties:
isGrounded: boolean = true;

// Add jump method:
jump(): void {
  if (this.isGrounded) {
    this.velocity.y = -this.config.jumpVelocity;
    this.isGrounded = false;
  }
}`,
          applied: false,
        },
      ],
    };
  }
  
  if (queryLower.includes('particle') || queryLower.includes('effect')) {
    return {
      text: `To add particle effects, you'll want to create a new ParticleSystem. This system will manage spawning, updating, and rendering particles. Here's a basic structure:`,
      codeBlocks: [
        {
          id: 'code-2',
          language: 'typescript',
          filename: 'client/systems/ParticleSystem.ts',
          code: `import { ClientSystem } from '@gotti/client';
import { SYSTEMS } from '../../shared/constants';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  color: string;
}

export default class ParticleSystem extends ClientSystem() {
  private particles: Particle[] = [];
  
  constructor() {
    super(SYSTEMS.PARTICLES);  // Add to constants.ts
  }
  
  spawn(x: number, y: number, count: number = 10): void {
    for (let i = 0; i < count; i++) {
      this.particles.push({
        x, y,
        vx: (Math.random() - 0.5) * 100,
        vy: (Math.random() - 0.5) * 100,
        life: 1.0,
        color: '#4ec9b0',
      });
    }
  }
  
  update(delta: number): void {
    this.particles = this.particles.filter(p => {
      p.x += p.vx * delta;
      p.y += p.vy * delta;
      p.life -= delta;
      return p.life > 0;
    });
  }
}`,
          applied: false,
        },
      ],
    };
  }

  if (queryLower.includes('input') && queryLower.includes('remap')) {
    return {
      text: `Here's how to remap your input keys in the InputSystem. I've updated the key bindings to use WASD for movement and Space for jumping:`,
      codeBlocks: [
        {
          id: 'code-input-remap',
          language: 'typescript',
          filename: 'client/systems/InputSystem.ts',
          code: `import { ClientSystem } from '@gotti/client';
import { SYSTEMS } from '../../shared/constants';

// Remapped input bindings (WASD + Space)
const KEY_MAP: Record<string, string> = {
  'w': 'up',
  's': 'down',
  'a': 'left',
  'd': 'right',
  ' ': 'jump',
};

export default class InputSystem extends ClientSystem() {
  private keysDown = new Set<string>();

  constructor() {
    super(SYSTEMS.INPUT);
  }

  initialize(): void {
    document.addEventListener('keydown', (e) => {
      const mapped = KEY_MAP[e.key.toLowerCase()];
      if (mapped) this.keysDown.add(mapped);
    });
    document.addEventListener('keyup', (e) => {
      const mapped = KEY_MAP[e.key.toLowerCase()];
      if (mapped) this.keysDown.delete(mapped);
    });
  }

  getInput() {
    return {
      up: this.keysDown.has('up'),
      down: this.keysDown.has('down'),
      left: this.keysDown.has('left'),
      right: this.keysDown.has('right'),
      jump: this.keysDown.has('jump'),
    };
  }
}`,
          applied: false,
        },
      ],
    };
  }

  if (queryLower.includes('add') && queryLower.includes('message') && queryLower.includes('type')) {
    return {
      text: `I've added PLAYER_ATTACK and PLAYER_RESPAWN message types along with ATTACK and HEALTH system IDs to the shared constants:`,
      codeBlocks: [
        {
          id: 'code-constants-update',
          language: 'typescript',
          filename: 'shared/constants.ts',
          code: `export enum MESSAGES {
  PLAYER_JOINED = 0,
  PLAYER_LEFT = 1,
  PLAYER_LIST = 2,
  PLAYER_SNAPSHOT = 10,
  // New message types
  PLAYER_ATTACK = 20,
  PLAYER_RESPAWN = 21,
}

export enum SYSTEMS {
  INPUT = 0,
  PREDICTION = 1,
  PLAYER_SYNC = 2,
  RENDER = 3,
  PLAYER_MANAGER = 10,
  PLAYER_MOVEMENT = 11,
  // New system IDs
  ATTACK = 20,
  HEALTH = 21,
}`,
          applied: false,
        },
      ],
    };
  }
  
  // Default response
  return {
    text: `I understand you're asking about: "${query}"\n\nThis is a stubbed response from the agent. In the full implementation, I would:\n\n1. **Retrieve relevant context** from your codebase using RAG\n2. **Analyze the current sandbox state** to understand your code structure\n3. **Generate tailored code** that integrates with your existing systems\n\nFor now, I'm showing you how the chat interface works. Try asking about:\n- Adding a jump mechanic\n- Creating particle effects\n- Implementing new systems`,
    codeBlocks: [],
  };
}

function chunkText(text: string, chunkSize: number): string[] {
  const chunks: string[] = [];
  for (let i = 0; i < text.length; i += chunkSize) {
    chunks.push(text.slice(i, i + chunkSize));
  }
  return chunks;
}
